import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import MachineDetail from './MachineDetail';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Settings, 
  Plus, 
  Trash2, 
  AlertCircle 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MachineManagement({ user }) {
  const { t } = useLanguage();
  const [machines, setMachines] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMachine, setNewMachine] = useState({ name: '' });
  const [creating, setCreating] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState(null);

  const fetchMachines = async () => {
    try {
      const response = await axios.get(`${API}/machines`);
      setMachines(response.data);
    } catch (error) {
      console.error('Error fetching machines:', error);
      toast.error('Failed to load machines');
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchMachines(), fetchDepartments()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleCreateMachine = async (e) => {
    e.preventDefault();

    if (!newMachine.name.trim()) {
      toast.error('Please enter a machine name');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/machines`, {
        name: newMachine.name.trim()
      });
      
      setMachines([...machines, response.data]);
      setNewMachine({ name: '' });
      toast.success('Machine created successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create machine';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteMachine = async (machineId, machineName) => {
    if (!window.confirm(`Are you sure you want to delete "${machineName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/machines/${machineId}`);
      setMachines(machines.filter(machine => machine.id !== machineId));
      toast.success('Machine deleted successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete machine';
      toast.error(message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleMachineClick = (machine) => {
    setSelectedMachine(machine);
  };

  const handleMachineUpdate = (updatedMachine) => {
    setMachines(prevMachines =>
      prevMachines.map(machine =>
        machine.id === updatedMachine.id ? updatedMachine : machine
      )
    );
    // Update selectedMachine if it's currently open
    if (selectedMachine && selectedMachine.id === updatedMachine.id) {
      setSelectedMachine(updatedMachine);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="machine-management">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('machineManagement')}</h1>
          <p className="text-gray-600 mt-1">{t('manageMachines')}</p>
        </div>
      </div>

      {/* Create New Machine */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-green-50 to-emerald-50">
        <CardHeader>
          <CardTitle className="text-lg font-medium text-gray-900">
            {t('addNewMachine')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateMachine} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="machine-name" className="text-sm font-medium text-gray-700">
                {t('machineName')}
              </Label>
              <Input
                id="machine-name"
                type="text"
                placeholder={t('enterMachineName')}
                value={newMachine.name}
                onChange={(e) => setNewMachine({ ...newMachine, name: e.target.value })}
                className="bg-white border-gray-200 focus:border-green-400 focus:ring-green-400"
                disabled={creating}
                data-testid="new-machine-name"
              />
            </div>
            <div className="flex items-end">
              <Button 
                type="submit" 
                disabled={creating || !newMachine.name.trim()}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                data-testid="create-machine-btn"
              >
                {creating ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Adding...</span>
                  </div>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Machine
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Machines List */}
      {machines.length === 0 ? (
        <Card className="border-2 border-dashed border-gray-300">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Settings className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Machines Yet</h3>
            <p className="text-gray-600 text-center max-w-md">
              Add your first machine to start organizing maintenance work by specific equipment.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {machines.map((machine) => {
            return (
              <Card 
              key={machine.id} 
              className="hover:shadow-lg transition-all duration-200 card-animation border-0 shadow-sm cursor-pointer"
              onClick={() => handleMachineClick(machine)}
            >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg font-medium text-gray-900 flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mr-3">
                          <Settings className="w-5 h-5 text-white" />
                        </div>
                        {machine.name}
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1 ml-13">
                        Added {formatDate(machine.created_at)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMachine(machine.id, machine.name);
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      data-testid={`delete-machine-${machine.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Machine Detail Modal */}
      {selectedMachine && (
        <MachineDetail
          machine={selectedMachine}
          onClose={() => setSelectedMachine(null)}
          onUpdate={handleMachineUpdate}
          user={user}
        />
      )}
    </div>
  );
}

export default MachineManagement;
